'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Script from 'next/script'
import Button from '@/components/ui/Button'
import { TURNSTILE_SCRIPT, useTurnstile } from '@/components/tools/runs/useTurnstile'
import { INDUSTRY_MAX, SEED_MAX } from '@/lib/topic-cluster/types'

type CreateResponse =
  | { ok: true; id: string; status: string; cached: boolean }
  | { ok: false; error: string; message?: string; retryAfterSeconds?: number }

const FIELD =
  'mt-2 w-full rounded-md border bg-bg px-3 py-2.5 text-fg outline-none transition-colors focus:border-accent/60'

/** Seed keyword plus optional site and industry context. */
export default function ClusterForm() {
  const router = useRouter()
  const [seed, setSeed] = useState('')
  const [siteUrl, setSiteUrl] = useState('')
  const [industry, setIndustry] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const turnstile = useTurnstile()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (pending) return
    setError(null)
    if (seed.trim().length < 2) {
      setError('Enter the keyword you want to build around.')
      return
    }
    if (turnstile.enabled && !turnstile.token) {
      setError('The anti-bot check is still running. Give it a second and try again.')
      return
    }
    setPending(true)
    try {
      const res = await fetch('/api/tools/runs/topic-cluster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seed,
          siteUrl: siteUrl.trim() || undefined,
          industry: industry.trim() || undefined,
          turnstileToken: turnstile.token ?? undefined,
        }),
      })
      const data = (await res.json()) as CreateResponse
      if (!data.ok) {
        setError(data.message ?? 'We could not start the build. Try again.')
        if (data.error === 'captcha_failed') turnstile.reset()
        setPending(false)
        return
      }
      router.push(`/tools/topic-cluster-builder/report/${data.id}`)
    } catch {
      setError('We could not reach the service. Check your connection and try again.')
      setPending(false)
    }
  }

  return (
    <form onSubmit={submit} className="card mt-10 p-6 sm:p-8" noValidate>
      {turnstile.enabled && (
        <Script src={TURNSTILE_SCRIPT} strategy="afterInteractive" onLoad={turnstile.render} />
      )}
      <label className="block" htmlFor="cluster-seed">
        <span className="text-sm font-medium text-fg">Seed keyword</span>
        <input
          id="cluster-seed"
          type="text"
          value={seed}
          onChange={(e) => setSeed(e.target.value)}
          placeholder="e.g. marketing automation"
          maxLength={SEED_MAX}
          autoComplete="off"
          aria-invalid={error ? true : undefined}
          aria-describedby="cluster-seed-hint"
          className={`${FIELD} ${error ? 'border-danger/50' : 'border-line'}`}
        />
        <span id="cluster-seed-hint" className="mt-1 block text-xs text-muted">
          A topic or a specific phrase. The builder tells you which it is.
        </span>
      </label>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="block" htmlFor="cluster-site">
          <span className="text-sm font-medium text-fg">
            A page on your site <span className="font-normal text-muted">(optional)</span>
          </span>
          <input
            id="cluster-site"
            type="url"
            inputMode="url"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://example.com/"
            autoComplete="url"
            spellCheck={false}
            className={`${FIELD} border-line`}
          />
          <span className="mt-1 block text-xs text-muted">
            Read once for who you are and who you serve. Not audited.
          </span>
        </label>
        <label className="block" htmlFor="cluster-industry">
          <span className="text-sm font-medium text-fg">
            Industry or audience <span className="font-normal text-muted">(optional)</span>
          </span>
          <input
            id="cluster-industry"
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. independent car dealers"
            maxLength={INDUSTRY_MAX}
            autoComplete="off"
            className={`${FIELD} border-line`}
          />
          <span className="mt-1 block text-xs text-muted">
            Steers the clusters toward the reader you want.
          </span>
        </label>
      </div>
      <div ref={turnstile.widgetRef} className="mt-4" />
      {error && (
        <p role="alert" className="mt-4 text-sm text-danger">
          {error}
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-4">
        <Button type="submit" size="lg" disabled={pending || !turnstile.ready}>
          {pending ? 'Starting the build' : 'Build the matrix'}
        </Button>
        <span className="text-sm text-muted">Free. No email required.</span>
      </div>
      <p className="mt-6 text-sm leading-relaxed text-muted">
        What happens: we ask Google Autocomplete what people type around your keyword, read your
        page if you gave one, then build the pillar, the clusters and the subtopics with an intent,
        a funnel stage and a content type on every row. Each subtopic says whether it came from a
        live suggestion or from the model.
      </p>
    </form>
  )
}
